import { setupGlobalApi } from '@/api-interceptor';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { FavoritesProvider } from '@/contexts/FavoritesContext';
import { ComparisonProvider } from '@/contexts/ComparisonContext';
import { Toaster } from '@/components/ui/toaster';
import App from '@/App'; // Import the main App component

// Configurar API interceptor
setupGlobalApi();

// Import Pages
import Home from '@/pages/Home';
import Properties from '@/pages/Properties';
import FinancialCenter from '@/pages/FinancialCenter';
import FuturaCleaning from '@/pages/FuturaCleaning';
import AboutUs from '@/pages/AboutUs';
import Contact from '@/pages/Contact';
import Services from '@/pages/Services';

// Import Components

import FloatingWhatsApp from '@/components/FloatingWhatsApp';
import TestimonialSlider from '@/components/TestimonialSlider';
import QuickSearch from '@/components/QuickSearch';
import InteractiveMap from '@/components/InteractiveMap';

import '@/index.css';

// Mobile Drag and Drop Polyfill
import { polyfill } from "mobile-drag-drop";
import { scrollBehaviourDragImageTranslateOverride } from "mobile-drag-drop/scroll-behaviour";

// Mobile Drag and Drop Polyfill (Simplified, @dnd-kit handles the Kanban)
polyfill({
    dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
    forcePolyfill: false
});

// FORCE NON-PASSIVE LISTENERS (NUCLEAR PATCH)
['touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach(eventName => {
    window.addEventListener(eventName, function (e) {
        // Intercept interaction with Kanban cards
        const isCard = e.target && e.target.closest ? e.target.closest('.kanban-card') : null;
        if (isCard) {
            // If dragging, we must own the event
            if (document.body.classList.contains('is-dragging')) {
                if (e.cancelable) e.preventDefault();
                e.stopPropagation();
            }
        }
    }, { passive: false, capture: true }); // Capture phase for priority
});

// Block contextual menus on cards (long press)
window.addEventListener('contextmenu', (e) => {
    if (e.target && e.target.closest && e.target.closest('.kanban-card')) {
        e.preventDefault();
    }
}, true);

// Wrapper for isolated React trees
const MountWrapper = ({ children }) => (
    <AuthProvider>
        <FavoritesProvider>
            <ComparisonProvider>
                <HashRouter>
                    {children}
                    <Toaster />
                </HashRouter>
            </ComparisonProvider>
        </FavoritesProvider>
    </AuthProvider>
);

// Mapping of DOM IDs to Components
const widgets = {
    // Full Pages
    'futura-page-home': Home,
    'futura-page-properties': Properties,
    'futura-page-financial': FinancialCenter,
    'futura-page-cleaning': FuturaCleaning,
    'futura-page-about': AboutUs,
    'futura-page-contact': Contact,
    'futura-page-services': Services,

    // Individual Components (for building with Elementor)
    'futura-comp-search': QuickSearch,
    'futura-comp-testimonials': TestimonialSlider,
    'futura-comp-map': InteractiveMap,
    'futura-comp-whatsapp': FloatingWhatsApp,
};

console.log('Futura System: Initializing...');

let mounted = false;

const getBuildHash = () => {
    const scripts = Array.from(document.querySelectorAll('script[src]')).map(s => s.src);
    const match = scripts.map(src => src.match(/index-([a-f0-9]+)\.js/)).find(Boolean);
    return match ? match[1] : 'unknown';
};

const logAssetsLoaded = () => {
    const scripts = Array.from(document.querySelectorAll('script[src]')).map(s => s.src);
    console.log('ASSETS LOADED', scripts);
    return scripts;
};

const showDeployMismatchBanner = () => {
    if (document.getElementById('deploy-mismatch-banner')) return;
    const banner = document.createElement('div');
    banner.id = 'deploy-mismatch-banner';
    banner.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:9999;background:#b91c1c;color:#fff;padding:10px 16px;font-family:system-ui,sans-serif;font-weight:700;text-align:center;';
    banner.textContent = 'Deploy parcial detectado. Assets desincronizados.';
    document.body.appendChild(banner);
};

const checkAssetMismatch = async (assets) => {
    try {
        const results = await Promise.all(assets.map(async (src) => {
            try {
                const res = await fetch(src, { method: 'HEAD', cache: 'no-store' });
                return res.ok;
            } catch (err) {
                return false;
            }
        }));
        if (results.some((ok) => !ok)) {
            showDeployMismatchBanner();
        }
    } catch (err) {
        console.warn('Asset mismatch check failed:', err);
    }
};

const logBackendDiagnostics = async () => {
    const endpoints = [
        '/api.php?action=get_user_plan',
        '/api.php?action=leads'
    ];

    await Promise.all(endpoints.map(async (url) => {
        try {
            const res = await fetch(url, { cache: 'no-store', credentials: 'include' });
            const contentType = res.headers.get('content-type') || '';
            const text = await res.text();
            console.log('BACKEND CHECK', url, {
                status: res.status,
                contentType,
                preview: text.slice(0, 200)
            });
        } catch (err) {
            console.warn('BACKEND CHECK FAILED', url, err);
        }
    }));
};

// Mount Logic for Widgets (WordPress Mode)
Object.keys(widgets).forEach(id => {
    const element = document.getElementById(id);
    if (element) {
        console.log(`Futura System: Mounting ${id}`);
        const Component = widgets[id];
        ReactDOM.createRoot(element).render(
            <MountWrapper>
                <Component />
            </MountWrapper>
        );
        mounted = true;
    }
});

// Error Boundary for debugging
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
        console.error("ErrorBoundary caught:", error, errorInfo);

        window.__lastAppError = {
            message: error?.message || String(error),
            stack: error?.stack || null,
            componentStack: errorInfo?.componentStack || null,
            time: new Date().toISOString()
        };
    }

    render() {
        const isDebug = (import.meta.env.MODE !== 'production') || (import.meta.env.VITE_SHOW_DEBUG_ERRORS === 'true');
        if (this.state.hasError) {
            return (
                <div style={{ padding: '20px', color: 'red', overflow: 'auto', maxHeight: '100vh', fontFamily: 'monospace' }}>
                    <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>Something went wrong.</h1>
                    <p style={{ marginBottom: '20px' }}>Please share this error message:</p>
                    <div style={{ background: '#f0f0f0', padding: '15px', borderRadius: '5px' }}>
                        <strong>{this.state.error && this.state.error.toString()}</strong>
                        {!isDebug && (
                            <div style={{ marginTop: '10px', fontWeight: 'bold' }}>
                                Error técnico detectado: {this.state.error?.message || 'Error'}
                            </div>
                        )}
                        {isDebug && (
                            <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
                                {this.state.errorInfo && this.state.errorInfo.componentStack}
                                {this.state.error && this.state.error.stack ? `\n${this.state.error.stack}` : ''}
                            </details>
                        )}
                    </div>
                    <button
                        onClick={() => { localStorage.clear(); window.location.reload(); }}
                        style={{ marginTop: '20px', padding: '10px 20px', background: '#333', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                    >
                        Clear Cache & Reload
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

// Fallback for Local Development (SPA Mode)
const rootElement = document.getElementById('root');
if (rootElement && !mounted) {
    console.log('Futura System: Mounting Full App (Local Dev Mode)');
    const assets = logAssetsLoaded();
    const buildHash = getBuildHash();
    console.log('APP VERSION', buildHash);
    checkAssetMismatch(assets);
    logBackendDiagnostics();
    ReactDOM.createRoot(rootElement).render(
        <ErrorBoundary>
            <App />
        </ErrorBoundary>
    );
}
